/**
 * ShareKit - Social Share Plugin v2
 * A modern, configurable social sharing library
 * 
 * @example V2 API (Recommended)
 * ```ts
 * import { ShareKit } from 'sharekit';
 * 
 * // Configure global defaults
 * ShareKit.configure({
 *   platforms: ['twitter', 'facebook', 'linkedin', 'copylink'],
 *   content: {
 *     url: 'https://example.com',
 *     title: 'Check this out!'
 *   }
 * });
 * 
 * // Create instances
 * const sticky = ShareKit.createStickyBar({ position: 'auto' });
 * const inline = ShareKit.createInline('#share-buttons');
 * 
 * // Update all instances at once (great for SPAs)
 * ShareKit.updateContent({ url: window.location.href });
 * ```
 * 
 * @example Legacy API (Still supported)
 * ```ts
 * import { SocialShare } from 'sharekit';
 * 
 * const share = new SocialShare({
 *   platforms: ['twitter', 'facebook', 'pinterest', 'copylink'],
 *   content: {
 *     url: 'https://example.com',
 *     title: 'Check this out!'
 *   },
 *   position: 'auto'
 * });
 * ```
 */

// ============================================
// V2 API - Main exports
// ============================================

// ShareKit Manager (main entry point)
export { ShareKit, ShareKitManager } from './core/ShareKit';
export type { ShareKitGlobalConfig, StickyBarOptions, InlineOptions } from './core/ShareKit';

// ShareInstance (individual share bar)
export { ShareInstance } from './core/ShareInstance';
export type { ShareInstanceConfig } from './core/ShareInstance';

// ============================================
// Legacy API - Backward compatibility
// ============================================

// SocialShare is now an alias for ShareInstance
export { SocialShare } from './core/ShareInstance';

// ============================================
// Types
// ============================================

export type {
  PlatformId,
  PositionMode,
  LayoutMode,
  LabelState,
  ShareContent,
  Platform,
  SocialShareConfig,
  SocialShareState,
  SocialShareEvent,
  EventCallback,
  BlockItemConfig,
  BlockShareConfig,
} from './types';

// ============================================
// Platform utilities
// ============================================

export {
  platforms,
  getPlatform,
  getPlatforms,
  isPlatformId,
  getAllPlatformIds,
} from './platforms';

// Individual platforms (for advanced usage)
export {
  twitter,
  facebook,
  pinterest,
  reddit,
  linkedin,
  vk,
  ok,
  weibo,
  qzone,
  line,
  kakao,
  naver,
  messenger,
  tumblr,
  plurk,
  copylink,
} from './platforms';

// ============================================
// Utility functions
// ============================================

export { copyToClipboard } from './utils/clipboard';
export { buildQueryString, buildShareUrl, ensureProtocol } from './utils/url';

// ============================================
// Configuration
// ============================================

export { DEFAULT_CONFIG, DEFAULT_LABELS, DEFAULT_BLOCK_CONFIG, mergeConfig } from './core/config';
